<template>
  <div class="spoiler-section">
    <ChooseCreatorModal
      v-if="showChooseMultipleCreatorsModal"
      :isVisible="true"
      :selectedCreatorIds="selectedCreatorIds"
      :collabsRemaining="collabsRemaining"
      :chooseCreatorIsSaving="chooseCreatorIsSaving"
      :communityId="communityId"
      :collabInput="collabInput"
      :invitedCreators="invitedCreatorsClient"
      @confirm="chooseMultipleCreators"
      @cancel="cancelModals"
      @close="showChooseMultipleCreatorsModal = false"
    />

    <SrpDetailsSummary class="spoiler-section__details-summary" v-model:is-opened="isOpened" :transition-duration="150">
      <template #heading="{ isOpened }">
        <div
          :style="{
            width: '100%',
            paddingBottom: isOpened ? 0 : '15px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }"
          class="global-h3 spoiler-section__summary"
        >
          <span><span>Creator Applications</span></span>
          <RouterLink
            v-if="isApplicationsShareVisible"
            class="spoiler-section__applications-share"
            :to="{ name: 'AnonymousCreatorApplicantsSummary', params: { communityId, collabInputId: collabInput.id } }"
            target="_blank"
            @click.stop
          >
            <IconEmbedded name="share-up_2" :size="18" color="#5b5b5b" />
            <span>share</span>
          </RouterLink>
        </div>

        <CreatorsRankingModal v-model:isVisible="isCreatorsRankingModalVisible" :allCreators="invitedCreatorsClient" :customerId="communityId" :collabInputId="collabInput.id" @save="load(true)" />
      </template>

      <template #details>
        <div class="spoiler-section__content">
          <NoteWithIcon v-if="selectedCreatorIds.length > 1" color="green" style="margin-bottom: 15px">
            <template #icon><IconEmbedded name="info-simple_4" :size="26" /></template>
            <template #text>
              <div>
                <div class="global-h4">You have selected multiple creators</div>
                <div>A separate creator visit will be created for each creator chosen. You will use a creator visit for each (so choosing 2 creators will use 2 creator visits)</div>
              </div>
            </template>
          </NoteWithIcon>

          <!--No creators applied yet-->
          <div v-if="invitedCreatorsClient?.length === 0" class="spoiler-section__notifications-list">
            <div class="spoiler-section__notifications-divider"></div>
            <!--Normal "posted" flow-->
            <template v-if="collabInput.postedToCreators || collabInput.readyToPost">
              <NoteWithIcon color="green">
                <template #icon><IconEmbedded name="info-simple_4" /></template>
                <template #text>
                  <div>
                    <h4 class="global-h4" style="margin-bottom: 3px">Hang tight while creators apply to your visit!</h4>
                    <span> Creators will review the visit details and apply. </span>
                  </div>
                </template>
              </NoteWithIcon>
            </template>
            <!-- / Normal "posted" flow-->
          </div>
          <!-- / No creators applied yet-->

          <!--Creators applied/accepted-->
          <div v-else class="spoiler-section__notifications-list">
            <div class="spoiler-section__notifications-divider"></div>

            <!-- Creators applied in Normal "posted" flow -->
            <template v-if="collabInput.postedToCreators || collabInput.readyToPost">
              <!-- Creators applied but more can still apply -->
              <template v-if="daysUntilCommunityIsNotified > 0">
                <NoteWithIcon color="blue" style="margin-bottom: 15px">
                  <template #icon><IconEmbedded name="bulb_2-5" /></template>
                  <template #text>
                    <div>
                      <h5 class="global-h5">We've sent this visit to creators in range.</h5>
                      <div>Creators can still apply but you can choose a creator at any time.</div>
                    </div>
                  </template>
                </NoteWithIcon>
              </template>
              <!-- / Creators applied but more can still apply -->

              <!-- Community should choose a creator -->
              <NoteWithIcon v-else color="blue" style="margin-bottom: 13px">
                <template #icon><img style="width: 65%; height: 65%" src="https://cdn.shrpa.com/images/misc/high-five.png" /></template>
                <template #text>
                  <div v-if="areCreatorsRanked">
                    <h4 class="global-h4">Creators Applied and Ranked!</h4>
                    <div>
                      Choose a creator (or multiple!) for this visit.<br />
                      Selecting multiple creators will duplicate your current visit and use an additional creator visit for each one.<br />
                      We have ranked the applicants to help you identify the best fit for your needs.
                    </div>
                  </div>
                  <div v-else-if="atLeastOneCreatorAccepted && !collabInput.collaboration.creatorId">
                    <h4 class="global-h4">Creators Applied!</h4>
                    <div>
                      Choose a creator (or multiple!) for this visit.<br />
                      Selecting multiple creators will duplicate your current visit and use an additional creator visit for each one.
                    </div>
                  </div>
                  <div v-else>
                    <h4 class="global-h4">You've selected a creator!</h4>
                    <div>You can review creators and save any you liked to your Saved Creators page for future collaborations.</div>
                  </div>
                </template>
              </NoteWithIcon>
              <!-- / Community should choose a creator -->
            </template>
            <!-- / Creators applied in Normal "posted" flow -->
          </div>
          <!-- /Creators applied-->

          <div v-if="invitedCreatorsClient?.length" class="spoiler-section__creator-sort-row">
            <select id="creator-invites-sort" v-model="creatorSortMode" class="spoiler-section__creator-sort-select">
              <option value="default">default sort</option>
              <option value="recentlyApplied">recently applied</option>
              <option value="firstApplied">first applied</option>
            </select>
          </div>

          <ul v-if="invitedCreatorsClient?.length" class="spoiler-section__creator-snippets-list">
            <PaidCreatorSummary
              class="spoiler-section__creator-snippet"
              v-for="creator in sortedInvitedCreators"
              :key="`creators-${creator.creatorId}`"
              :creator="creator"
              :customerId="communityId"
              :collabInputId="collabInput.id"
              :communityCanCoverLodging="collabInput.hotelNightsCovered > 0"
              :showRanking="true"
              :isSaveCreatorLinkVisible="Boolean(collabInput.collaboration.creatorId)"
              :isCreatorSaved="savedCreatorsPayload?.manuallySaved?.findIndex(c => c.shrpaCreatorId === creator.creatorId) > -1"
              :allApplications="invitedCreatorsClient"
              @saveCreator="async () => (savedCreatorsPayload = await CustomerSavedCreatorsRepo.loadAllData(communityId))"
              @unSaveCreator="async () => (savedCreatorsPayload = await CustomerSavedCreatorsRepo.loadAllData(communityId))"
              @sortChanged="handleSortChanged"
            >
              <input type="checkbox" :value="creator.creatorId" v-model="selectedCreatorIds" style="width: 20px" />
            </PaidCreatorSummary>
          </ul>

          <div
            :style="{
              paddingBottom: selectedCreatorIds.length > 0 ? '25px' : '0',
              marginBottom: selectedCreatorIds.length > 0 ? '25px' : 0,
              borderBottom: selectedCreatorIds.length > 0 ? '1px rgba(0,0,0,0.15) solid' : 'none',
            }"
            style="display: flex; justify-content: space-between"
          >
            <span
              >Have any questions or issues with a creator? Contact us anytime! <b><CopyText :text="'collabs@shrpa.com'" /></b
            ></span>
          </div>

          <!-- Manage buttons panel -->
          <div
            v-if="selectedCreatorIds.length > 0"
            :class="{
              'manage-buttons-panel': true,
              'manage-buttons-panel--stuck': isButtonsPanelStuck,
              'spoiler-section__manage-buttons-panel': true,
              'spoiler-section__manage-buttons-panel--stuck': isButtonsPanelStuck,
            }"
          >
            <div class="manage-buttons-panel__left-side"></div>

            <div class="manage-buttons-panel__right-side">
              <SrpButton
                :size="screenSize === 'mobile' ? 'small' : 'normal'"
                class="manage-buttons-panel__button"
                sidePaddings="large"
                :isDisabled="isSaving"
                @click="showChooseMultipleCreatorsModal = true"
              >
                {{ selectedCreatorIds.length > 1 ? `Finalize ${selectedCreatorIds.length} Creators` : "Finalize Choice" }}
              </SrpButton>
            </div>
          </div>
          <!-- / Manage buttons panel -->

          <div ref="intersectionDetectionTarget"></div>
        </div>
      </template>
    </SrpDetailsSummary>
  </div>
</template>

<script lang="ts">
import axios from "axios";
import CollabStatuses from "@logic/CollabStatuses";
import DateUtils from "@logic/DateUtils";
import { defineComponent, inject } from "vue";
import { RouterLink } from "vue-router";
import { mapState } from "pinia";
import FeatureFlags from "@logic/FeatureFlags";
import CustomerSavedCreatorsRepo from "@repos/CustomerSavedCreatorsRepo";

// Types
import CollabConsts, { CollabCreatorInput, CollabInput, CreatorCollabMatch, ChooseCreatorInputPayload } from "@contracts/collab";
import { ScreenSize } from "@contracts/screenSize";
import { CustomerSavedCreatorsPayload } from "@contracts/customerSavedCreators";

// Stores
import { useUserProfileStore } from "@stores/userProfileStore";

// Components
import ChooseCreatorModal from "./ChooseCreatorModal/index.vue";
import CopyText from "@components/CopyText.vue";
import CreatorsRankingModal from "./CreatorsRankingModal.vue";
import Loader from "@components/Loader/Loader.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import PaidCreatorSummary from "@components/PaidCreatorSummary.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import SrpDetailsSummary from "@components/ui/SrpDetailsSummary.vue";
import SrpModal from "@components/ui/SrpModal.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

export default defineComponent({
  name: "CreatorInvites",

  components: {
    IconEmbedded,
    ChooseCreatorModal,
    CopyText,
    CreatorsRankingModal,
    Loader,
    NoteWithIcon,
    PaidCreatorSummary,
    RouterLink,
    SrpButton,
    SrpDetailsSummary,
    SrpModal,
  },

  props: {
    communityId: { type: String, required: true },
    collabInput: { type: Object as () => CollabInput, required: true },
    collabIndex: { type: Number, required: true },
    reloadTrigger: { type: Number, required: false },
    collabsRemaining: { type: Number, required: true },
  },

  emits: ["selectMultipleCreators", "invitedCreatorsLoaded"],

  data() {
    return {
      screenSize: inject("screenSize") as ScreenSize,

      daysToApplyAfterPosted: CollabConsts.CollabInviteExpirationInDays,
      daysCreatorHasToCreateAdventures: CollabConsts.DaysCreatorHasToCreateAdventures,

      isSaving: false,
      showRequired: false,
      // Full invite with creator details
      invitedCreatorsClient: null as Array<CreatorCollabMatch>,
      inviteStatusMessage: null as string,

      showChooseMultipleCreatorsModal: false,
      chooseCreatorIsSaving: false,
      creatorIdInContext: null as string,
      creatorNameInContext: null as string,

      isOpened: true,

      isButtonsPanel2Stuck: false,

      selectedCreatorIds: [],

      intersectionObserver: null,
      isButtonsPanelStuck: false,

      isCreatorsRankingModalVisible: false,

      justChoseACreator: false,

      savedCreatorsPayload: null as CustomerSavedCreatorsPayload | null,

      FeatureFlags,
      CustomerSavedCreatorsRepo,

      creatorSortMode: "default" as "default" | "firstApplied" | "recentlyApplied",
    };
  },

  computed: {
    ...mapState(useUserProfileStore, ["isSuperOrSalesUser"]),
    sortedInvitedCreators(): Array<CreatorCollabMatch> {
      const list = this.invitedCreatorsClient;
      if (!list?.length) return [];
      if (this.creatorSortMode === "default") return [...list];
      const getApplicationTimestampMs = (c: CreatorCollabMatch): number => {
        const raw = c.collabCreatorInput?.acceptedDateTime ?? c.collabCreatorInput?.createdOn ?? null;
        if (!raw) return 0;
        const t = new Date(raw).getTime();
        return Number.isNaN(t) ? 0 : t;
      };
      return [...list].sort((a, b) => {
        const ta = getApplicationTimestampMs(a);
        const tb = getApplicationTimestampMs(b);
        if (ta !== tb) {
          return this.creatorSortMode === "firstApplied" ? ta - tb : tb - ta;
        }
        return a.creatorId.localeCompare(b.creatorId);
      });
    },
    atLeastOneCreatorAccepted(): boolean {
      return this.invitedCreatorsClient?.some(i => i.collabCreatorInput.acceptedDateTime) === true;
    },
    areCreatorsRanked(): boolean {
      return this.invitedCreatorsClient?.some(i => i.collabCreatorInput.rank) === true;
    },
    // Only applies for application flow currently
    daysUntilCommunityIsNotified(): number {
      if (!this.collabInput?.postedToCreators) return -1;

      let postedDate = new Date(this.collabInput.postedToCreators);
      let notifyDate = DateUtils.addDays(postedDate, this.daysToApplyAfterPosted);
      let daysBetween = DateUtils.daysBetween(new Date(), notifyDate);
      var roundedDays = Math.round(daysBetween);
      return roundedDays;
    },
    isCompleted(): boolean {
      if (!this.collabInput?.collaboration) return false;
      return this.collabInput.collaboration.status === CollabStatuses.CompleteStatusId || !!this.collabInput.collaboration.completedDate;
    },
    isApplicationsShareVisible(): boolean {
      const hasApplicants = Array.isArray(this.invitedCreatorsClient) && this.invitedCreatorsClient.length > 0;
      if (!hasApplicants) return false;
      if (this.collabInput?.collaboration?.status === CollabStatuses.CompleteStatusId) return false;
      if (this.collabInput?.collaboration?.completedDate) return false;
      const creatorId = this.collabInput?.collaboration?.creatorId;
      const creatorChosenDate = this.collabInput?.collaboration?.creatorChosenDate;
      // 5-day window only applies once a creator is actually chosen
      if (!creatorId || !creatorChosenDate) return true;
      const chosen = new Date(creatorChosenDate);
      if (Number.isNaN(chosen.getTime()) || chosen.getFullYear() <= 2000) return true;
      return DateUtils.addDays(chosen, 5).getTime() > Date.now();
    },
  },

  watch: {
    // Resets the state since they changed collabs
    collabIndex: function (newVal, oldVal) {
      this.creatorSortMode = "default";
      this.setDefaultIsOpened();
      this.load(true);
      this.inviteStatusMessage = null;
      this.intersectionObserver.disconnect();
      this.$nextTick(this.setIntersectionObservers);
    },
    isOpened() {
      this.intersectionObserver.disconnect();
      this.$nextTick(this.setIntersectionObservers);
    },
    reloadTrigger: function (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.load(true);
      }
    },
  },

  async mounted() {
    this.setDefaultIsOpened();
    await this.setIntersectionObservers();
    await this.load(true);
  },

  beforeUnmount() {
    this.intersectionObserver.disconnect();
  },

  methods: {
    // Called by the parent
    showRankCreators() {
      this.isCreatorsRankingModalVisible = true;
    },
    async setIntersectionObservers(): Promise<void> {
      // Detect whether button panels are stuck (is "sticky" position applied) to move it a bit higher to avoid overlapping with mobile bottom navigation panel
      this.intersectionObserver = new IntersectionObserver(
        ([e]) => (this.isButtonsPanelStuck = e.intersectionRatio === 0 && (this.$refs.intersectionDetectionTarget as HTMLDivElement)?.getBoundingClientRect().top > 0),
        { threshold: 0.1 }
      );
      await this.$nextTick();
      if (this.$refs.intersectionDetectionTarget) {
        this.intersectionObserver.observe(this.$refs.intersectionDetectionTarget as Element);
      }
    },
    async load(clearListBeforeLoading: boolean) {
      if (clearListBeforeLoading) {
        this.invitedCreatorsClient = null;
      }
      let uri = `${import.meta.env.VITE_API_URL}/collabs/${this.communityId}/inputs/${this.collabInput.id}/invites`;
      const { data } = await axios.get(uri);
      this.invitedCreatorsClient = data;
      // Emit this back up so the peer CollabSummary component can use it to send users down to this section
      this.$emit("invitedCreatorsLoaded", data);

      // If a creator is chosen, load the Saved Creators
      if (this.collabInput.collaboration.creatorId) {
        this.savedCreatorsPayload = await CustomerSavedCreatorsRepo.loadAllData(this.communityId);
      }
    },
    setDefaultIsOpened() {
      if (this.justChoseACreator) {
        this.isOpened = true;
      } else {
        this.isOpened = !(+this.collabInput.collaboration.status >= +CollabStatuses.CreatorsChosenStatusId);
      }
    },
    getFormattedVisitTime(creatorInput: CollabCreatorInput): string {
      return DateUtils.formatDateRange(creatorInput.proposedVisitDateTime, creatorInput.numberOfDaysStaying);
    },
    cancelModals() {
      this.showChooseMultipleCreatorsModal = false;
      this.creatorIdInContext = null;
      this.creatorNameInContext = null;
    },
    async chooseMultipleCreators(chooseCreatorInputs: ChooseCreatorInputPayload): Promise<void> {
      this.chooseCreatorIsSaving = true;
      let uri = `${import.meta.env.VITE_API_URL}/collabs/${this.communityId}/inputs/${this.collabInput.id}/invites/choose-creator/v3?creatorIds=${this.selectedCreatorIds.join("&creatorIds=")}`;
      const { data } = await axios.post(uri, chooseCreatorInputs);
      // No need to update the state since the parent will reload all the collabs

      this.selectedCreatorIds = [];
      this.chooseCreatorIsSaving = false;
      this.justChoseACreator = true;
      this.showChooseMultipleCreatorsModal = false;
      // Now have a final "Next steps slide so let the modal call close"
      // This causes the parent to reload all the collabs
      this.$emit("selectMultipleCreators", this.communityId);
    },
    handleSortChanged(creatorId: string, newSort: number): void {
      // Update locally first for immediate button state update
      const creator = this.invitedCreatorsClient?.find(c => c.creatorId === creatorId);
      if (creator) {
        creator.collabCreatorInput.sort = newSort;
      }
      // Then reload from server (without nulling the array, so no loading flash)
      this.load(false);
    },
  },
});
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";
@import "@/scss/screen-size-ranges.scss";
// IMPORTANT: Keep this import order. Both classes are applied on the same action panel:
// manage-buttons-panel sets shared layout styles, and spoiler-section must come after to
// preserve its `position: sticky` override for the floating Finalize Choice button.
@import "./styles/manage-buttons-panel";
@import "./styles/spoiler-section";

.spoiler-section {
  &__applications-share {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    margin-right: 4px;
    color: #5b5b5b;
    font: 400 14px / 18px sans-serif;
    text-decoration: none;
    position: relative;
    z-index: 3;

    span {
      text-decoration: underline;
      text-decoration-color: rgba(91, 91, 91, 0.5);
      text-underline-offset: 2px;
      text-decoration-thickness: 1px;
      text-decoration-style: dashed;
    }

    &:hover span {
      text-decoration-color: transparent;
    }
  }

  &__creator-sort-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: nowrap;
    gap: 8px;
    margin-bottom: 16px;
  }

  &__creator-sort-label {
    margin: 0;
    flex-shrink: 0;
    font-weight: 600;
    white-space: nowrap;
  }

  &__creator-sort-select {
    flex-shrink: 0;
    padding: 6px 10px;
    border-radius: 4px;
    border: 1px rgba(0, 0, 0, 0.2) solid;
    background: #fff;
  }
}

// ============================================================================

.ui.form {
  .feedback {
    margin-top: 25px;

    .section-title {
      margin: 0 0 5px 0 !important;
    }
    .rating-section {
      margin: 5px 0 15px 0 !important;
    }
    .feedback-text {
      height: 80px;
      min-height: 50px;
      margin-bottom: 10px;
    }
  }
}
.accepted {
  color: $brand-color;
}
.declined {
  color: $orange;
}
.invite-revoked {
  color: $orange;
  font-weight: 800;
}
.invite-date {
  margin-bottom: 1em;
}
.not-invited-yet {
  color: $orange;
}

.hands {
  width: 30px;
  height: 30px;
  margin-right: 10px;
  vertical-align: middle;
  display: inline;
}

.close {
  z-index: 20;
  cursor: pointer;
  position: absolute;
  right: 0;
  top: -10px;
  right: -10px;
}
</style>
